import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { propertymarkArticle, type ArticleImage } from "src/data/propertymarkArticle";

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function ArticleImageBlock({ image }: { image: ArticleImage }): JSX.Element {
  return (
    <figure className="my-8">
      <img
        src={image.src}
        alt={image.alt}
        className="w-full aspect-[16/10] object-cover border border-solid border-[rgba(0,0,0,0.06)]"
        loading="lazy"
      />
      {image.caption ? (
        <figcaption className="font-manrope text-xs text-text-slate mt-2">
          {image.caption}
        </figcaption>
      ) : null}
    </figure>
  );
}

export default function PropertymarkArticlePage(): JSX.Element {
  const { title, date, venue, excerpt, speakers, heroImage, content } =
    propertymarkArticle;

  return (
    <>
      <Helmet>
        <title>{title} | Letting Engine</title>
        <meta name="description" content={excerpt} />
        <meta property="og:image" content={heroImage.src} />
      </Helmet>

      <article className="pt-40 pb-24 px-[5%]">
        <div className="max-w-[800px] mx-auto">
          <Link
            to="/"
            className="font-manrope text-sm text-text-slate hover:text-brand-yellow transition-colors mb-12 block"
          >
            ← Back to home
          </Link>

          <span className="font-manrope text-[10px] uppercase tracking-[0.2em] bg-brand-yellow text-text-obsidian px-2 py-1 inline-block mb-4">
            Industry news
          </span>

          <time
            dateTime={date}
            className="font-manrope text-xs uppercase tracking-wider text-text-slate block mb-4"
          >
            {formatDate(date)} · {venue}
          </time>

          <h1 className="font-teko text-5xl md:text-6xl text-text-obsidian leading-tight mb-8 tracking-[-1px]">
            {title}
          </h1>

          <figure className="mb-10">
            <img
              src={heroImage.src}
              alt={heroImage.alt}
              className="w-full aspect-[16/10] object-cover border border-solid border-[rgba(0,0,0,0.06)]"
              loading="eager"
            />
            {heroImage.caption ? (
              <figcaption className="font-manrope text-xs text-text-slate mt-2">
                {heroImage.caption}
              </figcaption>
            ) : null}
          </figure>

          <p className="font-manrope text-lg text-text-slate leading-relaxed mb-10">
            {excerpt}
          </p>

          <div className="mb-10 p-5 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.06)]">
            <p className="font-manrope text-xs uppercase tracking-wider text-text-slate mb-3">
              Featured speakers
            </p>
            <ul className="font-manrope text-sm text-text-obsidian space-y-1">
              {speakers.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          </div>

          <div className="font-manrope text-base leading-relaxed text-text-slate">
            {content.map((block, index) =>
              block.type === "paragraph" ? (
                <p key={`p-${index}`} className="mb-6 last:mb-0">
                  {block.text}
                </p>
              ) : (
                <ArticleImageBlock key={`img-${block.src}`} image={block} />
              )
            )}
          </div>

          <div className="mt-16 pt-8 border-t border-t-solid border-t-[rgba(0,0,0,0.1)]">
            <p className="font-manrope text-sm text-text-slate mb-4">
              Interested in our sales, lettings, or landlord services?
            </p>
            <Link
              to="/contact-us"
              className="font-teko text-xl uppercase text-brand-yellow border-b border-brand-yellow hover:text-text-obsidian hover:border-text-obsidian transition-colors"
            >
              Get in touch
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}
